// src/pages/support/index.tsx
import React, { useState, useEffect } from 'react';
import { Card, Nav, Alert } from 'react-bootstrap';
import { MessageSquare, Clock } from 'lucide-react';
import { useAuth } from '../../components/auth/hooks/useAuth';
import { supportService } from '../../services/supportService';
import ContentSkeleton from '../../components/loading/ContentSkeleton';
import { SupportForm } from '../../components/support/SupportForm';
import { TicketHistory } from '../../components/support/TicketHistory';
import type { TicketResponse } from '../../types/support';
import { PageTransition } from '../../components/layout';


type ActiveTab = 'new' | 'history';

const SupportPage: React.FC = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState<ActiveTab>('new');
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [initialTickets, setInitialTickets] = useState<TicketResponse | null>(null);

    useEffect(() => {
        if (!user) return;

        const initializePage = async () => {
            try {
                const initialData = await supportService.getTickets(1, false);
                setInitialTickets(initialData);
            } catch (error) {
                console.error('Failed to initialize support page:', error);
            } finally {
                setIsPageLoading(false);
            }
        };

        initializePage();
    }, [user]);

    const handleTabChange = (eventKey: string | null) => {
        if (!eventKey) return;

        if (eventKey === 'history') {
            setIsHistoryLoading(true);
        }
        setActiveTab(eventKey as ActiveTab);
    };

    const handleSubmit = async (formData: FormData) => {
        if (!user) return;

        formData.append('userEmail', user.email);
        formData.append('userFullName', user.fullName || user.full_name || '');
        formData.append('organizationId', user.organizationId);
        formData.append('userRole', user.role);

        await supportService.createTicket(formData);
    };

    if (!user) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <Alert variant="warning">Please log in to access support.</Alert>
                </div>
            </PageTransition>
        );
    }

    if (isPageLoading) {
        return (
            <div className="page-container">
                <div className="mb-4">
                    <h5 className="mb-3">Support</h5>
                    <ContentSkeleton />
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="mb-4">
                <h5 className="mb-3">Support</h5>

                <Nav
                    variant="tabs"
                    // className="mb-3"
                    activeKey={activeTab}
                    onSelect={handleTabChange}
                >
                    <Nav.Item>
                        <Nav.Link eventKey="new" className="d-flex align-items-center gap-2">
                            <MessageSquare size={16} />
                            New Request
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link eventKey="history" className="d-flex align-items-center gap-2">
                            <Clock size={16} />
                            My Tickets
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                        {activeTab === 'new' ? (
                            <SupportForm onSubmit={handleSubmit} />
                        ) : (
                            <TicketHistory
                                initialData={initialTickets}
                                isLoading={isHistoryLoading}
                                onLoadingChange={setIsHistoryLoading}
                            />
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
};

export default SupportPage;